import { useCallback, useMemo, useState } from 'react';
import useUser from './useUser';
import type { StrategyRecord } from '../types/account';
import {
  clearAccountLibraryCache,
  getAccountLibraryUserCacheKey,
} from '../utils/accountLibraryCache';
import { getPublishStrategyId } from '../utils/accountLibraryStrategyActions';

export type StrategyShareType = 'public' | 'private';

export interface StrategyShareOptions {
  allowClone?: boolean;
  forceRegenerate?: boolean;
  priceAmountCents?: number;
  pricingModel?: string;
}

export interface StrategyShareResult {
  alreadyPublished?: boolean;
  isPrivate?: boolean;
  isPublic?: boolean;
  marketplaceListingId?: string | null;
  shareToken?: string;
  shareUrl?: string;
  success: boolean;
}

interface UseStrategyMarketplaceShareOptions {
  onStrategyMarketplaceChange?: (
    strategy: StrategyRecord,
    marketplace: StrategyRecord['marketplace'],
  ) => void;
}

export function useStrategyMarketplaceShare({
  onStrategyMarketplaceChange,
}: UseStrategyMarketplaceShareOptions = {}) {
  const { getApiToken, user, userProfile } = useUser();
  const userCacheKey = useMemo(
    () => getAccountLibraryUserCacheKey(user, userProfile),
    [user, userProfile],
  );
  const [shareStrategy, setShareStrategy] = useState<StrategyRecord | null>(null);

  const openShareStrategy = useCallback((strategy: StrategyRecord): void => {
    setShareStrategy(strategy);
  }, []);

  const closeShareStrategy = useCallback((): void => {
    setShareStrategy(null);
  }, []);

  const updateMarketplaceState = useCallback((
    strategy: StrategyRecord,
    marketplace: StrategyRecord['marketplace'],
  ): void => {
    clearAccountLibraryCache((key) => key.startsWith(`account-library:${userCacheKey}:`));
    onStrategyMarketplaceChange?.(strategy, marketplace);
    setShareStrategy((current) => (
      current?.aiStrategyId === strategy.aiStrategyId
        ? { ...current, marketplace }
        : current
    ));
  }, [onStrategyMarketplaceChange, userCacheKey]);

  const makePublic = useCallback(async (
    _unusedPassword?: unknown,
    shareType: StrategyShareType = 'public',
    options: StrategyShareOptions = {},
  ): Promise<boolean | StrategyShareResult> => {
    if (!shareStrategy) return false;

    const token = await getApiToken();
    if (!token) return false;

    const strategyId = getPublishStrategyId(shareStrategy);
    const apiBase = process.env.REACT_APP_API_URL || '';
    const allowClone = options.allowClone !== false;

    if (shareType === 'private') {
      const response = await fetch(`${apiBase}/strategies/${strategyId}/share`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          forceRegenerate: options.forceRegenerate === true,
          settings: {
            allowClone: true,
            allowCodeView: true,
            requirePassword: true,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create private link (${response.status})`);
      }

      const result = await response.json() as { shareToken?: string; shareUrl?: string };
      const shareUrl = result.shareUrl?.startsWith('http')
        ? result.shareUrl
        : `${window.location.origin}${result.shareUrl || ''}`;

      return {
        success: true,
        isPrivate: true,
        shareToken: result.shareToken || '',
        shareUrl,
      };
    }

    const updateResponse = await fetch(`${apiBase}/strategies/${strategyId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isPublic: true,
        sharingSettings: {
          allowClone,
          allowCodeView: allowClone,
        },
      }),
    });

    if (!updateResponse.ok) {
      throw new Error(`Unable to update strategy sharing (${updateResponse.status})`);
    }

    const publishResponse = await fetch(`${apiBase}/marketplace/listings/publish`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        strategyId,
        tags: {},
        protectCode: !allowClone,
        ...(options.pricingModel && { pricingModel: options.pricingModel }),
        ...(options.priceAmountCents && { priceAmountCents: options.priceAmountCents }),
      }),
    });

    if (!publishResponse.ok && publishResponse.status !== 409) {
      const errorData = await publishResponse.json().catch(() => ({})) as { error?: string; message?: string };
      throw new Error(errorData.message || errorData.error || `Failed to publish strategy (${publishResponse.status})`);
    }

    const result = await publishResponse.json().catch(() => ({})) as {
      alreadyPublished?: boolean;
      id?: string;
      listingId?: string;
    };
    const marketplaceListingId = result.id || result.listingId || shareStrategy.marketplace?.marketplaceListingId || strategyId;
    const marketplace: StrategyRecord['marketplace'] = {
      isInMarketplace: true,
      marketplaceListingId,
    };

    updateMarketplaceState(shareStrategy, marketplace);

    return {
      success: true,
      isPublic: true,
      marketplaceListingId,
      alreadyPublished: result.alreadyPublished || publishResponse.status === 409,
    };
  }, [getApiToken, shareStrategy, updateMarketplaceState]);

  const makePrivate = useCallback(async (): Promise<boolean> => {
    if (!shareStrategy?.marketplace?.marketplaceListingId) return false;

    const token = await getApiToken();
    if (!token) return false;

    const strategyId = getPublishStrategyId(shareStrategy);
    const listingId = shareStrategy.marketplace.marketplaceListingId;
    const apiBase = process.env.REACT_APP_API_URL || '';

    const deleteResponse = await fetch(`${apiBase}/marketplace/listings/${listingId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!deleteResponse.ok && deleteResponse.status !== 404) {
      const errorData = await deleteResponse.json().catch(() => ({})) as { error?: string; message?: string };
      throw new Error(errorData.message || errorData.error || `Failed to remove listing (${deleteResponse.status})`);
    }

    const updateResponse = await fetch(`${apiBase}/strategies/${strategyId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isPublic: false }),
    });

    if (!updateResponse.ok) {
      throw new Error(`Marketplace listing removed, but privacy update failed (${updateResponse.status})`);
    }

    updateMarketplaceState(shareStrategy, {
      isInMarketplace: false,
      marketplaceListingId: null,
    });

    return true;
  }, [getApiToken, shareStrategy, updateMarketplaceState]);

  return {
    closeShareStrategy,
    makePrivate,
    makePublic,
    openShareStrategy,
    shareStrategy,
  };
}
